/**
 * Normalize SignalK numeric readings for dashboard display.
 * SignalK uses SI units (meters, Kelvin, Hz, m/s, etc.); widgets often show imperial.
 */

const METERS_TO_FEET = 3.280839895;

/** Depth paths that measure the same quantity with different offsets/sources. */
const DEPTH_PATH_FALLBACKS = {
  'environment.depth.belowKeel': [
    'environment.depth.belowTransducer',
    'environment.depth.belowSurface',
    'navigation.depthBelowTransducer',
  ],
  'environment.depth.belowTransducer': [
    'environment.depth.belowKeel',
    'environment.depth.belowSurface',
    'navigation.depthBelowTransducer',
  ],
  'environment.depth.belowSurface': [
    'environment.depth.belowTransducer',
    'environment.depth.belowKeel',
    'navigation.depthBelowTransducer',
  ],
  'navigation.depthBelowTransducer': [
    'environment.depth.belowTransducer',
    'environment.depth.belowKeel',
    'environment.depth.belowSurface',
  ],
};

function normalizeUnitToken(unit) {
  return String(unit || '')
    .trim()
    .toLowerCase()
    .replace(/°/g, '')
    .replace(/\s+/g, '');
}

/** Convert a temperature reading to Kelvin using SignalK unit metadata when present. */
function temperatureToKelvin(num, signalKUnit) {
  const u = normalizeUnitToken(signalKUnit);
  if (!u) return null;
  if (u === 'k' || u === 'kelvin') return num;
  if (u === 'degc' || u === 'c' || u === 'celsius') return num + 273.15;
  if (u === 'degf' || u === 'f' || u === 'fahrenheit') return (num - 32) * (5 / 9) + 273.15;
  return null;
}

/**
 * Infer Kelvin from a numeric reading. SignalK stores temperatures in Kelvin;
 * some legacy sources send Celsius without unit metadata.
 */
function inferKelvin(num, path, signalKUnit) {
  const fromUnit = temperatureToKelvin(num, signalKUnit);
  if (fromUnit !== null) return fromUnit;

  const isSkTempPath =
    path.startsWith('environment.') ||
    path.startsWith('propulsion.') ||
    path.includes('temperature') ||
    path.includes('Temperature');

  if (isSkTempPath) {
    // Typical marine Kelvin readings (water ~273–308 K, engine ~320–400 K)
    if (num >= 170 && num <= 450) return num;
    // Legacy Celsius without unit metadata
    if (num > -60 && num < 100) return num + 273.15;
    return num;
  }

  if (num >= 170 && num <= 450) return num;
  if (num > -60 && num < 100) return num + 273.15;
  return num;
}

/** Normalize depth to meters using SignalK unit metadata when present. */
function depthToMeters(num, signalKUnit) {
  const u = normalizeUnitToken(signalKUnit);
  if (u === 'ft' || u === 'feet' || u === 'foot') return num / METERS_TO_FEET;
  if (u === 'fathoms' || u === 'fathom') return num * 1.8288;
  return num;
}

function depthFallbackCandidates(dataPath) {
  if (!dataPath) return [];
  if (DEPTH_PATH_FALLBACKS[dataPath]) return DEPTH_PATH_FALLBACKS[dataPath];
  if (dataPath.includes('depth') || dataPath.includes('Depth')) {
    return [
      'environment.depth.belowTransducer',
      'environment.depth.belowKeel',
      'environment.depth.belowSurface',
      'navigation.depthBelowTransducer',
    ].filter((p) => p !== dataPath);
  }
  return [];
}

const DATA_SOURCE_MODES = new Set(['signalk', 'database', 'demo', 'live']);

/** True if a string looks like a dotted SignalK path (not a data mode like "signalk"). */
function looksLikeSignalKPath(value) {
  if (typeof value !== 'string' || !value.includes('.')) return false;
  return !DATA_SOURCE_MODES.has(value.trim().toLowerCase());
}

function firstDataPathFromArray(paths) {
  if (!Array.isArray(paths) || paths.length === 0) return '';
  const first = paths[0];
  if (typeof first === 'string') return first;
  if (first && typeof first === 'object') {
    return first.path || first.dataPath || first.data_path || '';
  }
  return '';
}

/** Mode tokens stored in data_source — not valid SignalK paths. */
export function pickWidgetSaveDataPath(widget) {
  if (!widget) return '';
  const candidates = [
    widget.data_path,
    widget.dataPath,
    widget.dataSource,
    widget.config?.dataPath,
    widget.config?.dataSource,
    widget.display_config?.dataPath,
  ];
  for (const c of candidates) {
    if (looksLikeSignalKPath(c)) return String(c).trim();
  }
  return '';
}

/**
 * Resolve the SignalK data path from a widget record.
 * WidgetEditor historically stored the path in `dataSource` / `data_source` instead of `data_path`.
 */
export function resolveWidgetDataPath(widget) {
  if (!widget) return '';
  const fromArray = firstDataPathFromArray(widget.dataPaths || widget.config?.dataPaths);
  const candidates = [
    widget.data_path,
    widget.dataPath,
    widget.config?.dataPath,
    widget.display_config?.dataPath,
    fromArray,
    widget.config?.dataSource,
    widget.data_source,
    widget.dataSource,
  ];
  for (const c of candidates) {
    if (looksLikeSignalKPath(c)) return String(c).trim();
  }
  const fallback = widget.data_path || widget.dataPath || fromArray || '';
  return typeof fallback === 'string' ? fallback.trim() : fallback;
}

const ELECTRICAL_SEMANTIC_ALIASES = /^(house|starter|main|primary|secondary)$/i;

/** True when path names a specific bank (e.g. 279-second), not house/starter alias. */
export function isConcreteElectricalInstancePath(dataPath) {
  const m = matchElectricalInstancePath(dataPath);
  if (!m) return false;
  return !ELECTRICAL_SEMANTIC_ALIASES.test(m[2]);
}

function parseNumber(rawValue) {
  if (rawValue === null || rawValue === undefined) return null;
  if (typeof rawValue === 'number' && !Number.isNaN(rawValue)) return rawValue;
  if (typeof rawValue === 'string' && rawValue.trim() !== '') {
    const trimmed = rawValue.trim();
    const n = parseFloat(trimmed);
    if (Number.isNaN(n)) return null;
    if (String(n) === trimmed) return n;
    if (/^-?\d+(\.\d+)?$/.test(trimmed)) return n;
    // Live stream / JSON sometimes sends numeric strings with float noise
    if (/^-?\d+(\.\d+)?([eE][+-]?\d+)?$/.test(trimmed)) return n;
  }
  return null;
}

/** Read widget decimal precision from config (decimals, precision, or decimalPlaces). */
export function coerceWidgetDecimals(widgetConfig = {}, fallback = 1) {
  if (!widgetConfig || typeof widgetConfig !== 'object') return fallback;
  const raw =
    widgetConfig.decimals ?? widgetConfig.precision ?? widgetConfig.decimalPlaces;
  if (raw === undefined || raw === null || raw === '') return fallback;
  const n = parseInt(raw, 10);
  return Number.isFinite(n) && n >= 0 ? n : fallback;
}

/** Normalize widget config from API shapes (config, display_config, JSON string). */
export function resolveWidgetConfig(widget = {}) {
  let cfg = widget?.config ?? widget?.display_config ?? widget?.displayConfig ?? {};
  if (typeof cfg === 'string') {
    try {
      cfg = JSON.parse(cfg);
    } catch {
      cfg = {};
    }
  }
  return cfg && typeof cfg === 'object' && !Array.isArray(cfg) ? cfg : {};
}

/** Format a widget reading for display, honoring configured decimal places. */
export function formatWidgetDisplayValue(value, widgetConfig = {}, fallbackDecimals = 1) {
  if (value === null || value === undefined || value === '--' || value === 'No Data') {
    return '--';
  }
  const decimals = coerceWidgetDecimals(widgetConfig, fallbackDecimals);
  const scalar = normalizeSignalkScalar(value);
  if (scalar === null) return '--';
  const num = typeof scalar === 'number' ? scalar : parseNumber(scalar);
  if (num !== null && Number.isFinite(num)) {
    return num.toFixed(decimals);
  }
  return String(scalar);
}

export function parseWidgetNumericValue(value) {
  if (value === null || value === undefined || value === '--') return null;
  const scalar = normalizeSignalkScalar(value);
  if (scalar === null) return null;
  if (typeof scalar === 'number' && Number.isFinite(scalar)) return scalar;
  return parseNumber(scalar);
}

/**
 * Unwrap plugin/DB shapes into a display scalar (string, number, or boolean).
 * Handles { raw: "mppt active" }, { name, id }, nested .value, etc.
 */
export function normalizeSignalkScalar(value) {
  if (value === null || value === undefined) return null;
  if (typeof value === 'boolean') return value ? 'On' : 'Off';
  if (typeof value === 'number' && !Number.isNaN(value)) return value;
  if (typeof value === 'string') {
    const t = value.trim();
    return t === '' ? null : t;
  }
  if (typeof value !== 'object') return String(value);

  if (Object.prototype.hasOwnProperty.call(value, 'raw')) {
    return normalizeSignalkScalar(value.raw);
  }
  if (value.name != null && (typeof value.name === 'string' || typeof value.name === 'number')) {
    return String(value.name);
  }
  if (typeof value.id === 'string' || typeof value.id === 'number') {
    return String(value.id);
  }
  if (Object.prototype.hasOwnProperty.call(value, 'value')) {
    const inner = normalizeSignalkScalar(value.value);
    if (inner !== null) return inner;
  }
  try {
    return JSON.stringify(value);
  } catch {
    return String(value);
  }
}

function headingToDegrees(value, unitHint = '') {
  const num = toCoordinateNumber(value);
  if (num === null) return null;
  const u = normalizeUnitToken(unitHint);
  if (u === 'rad' || u === 'radian') return ((num * 180) / Math.PI + 360) % 360;
  // SignalK often publishes heading in radians without unit metadata
  if (Math.abs(num) <= 6.5) return ((num * 180) / Math.PI + 360) % 360;
  return ((num % 360) + 360) % 360;
}

function speedToKnots(value, unitHint = '') {
  const num = toCoordinateNumber(value);
  if (num === null) return null;
  const u = normalizeUnitToken(unitHint);
  if (u === 'kn' || u === 'kts' || u === 'kt') return num;
  if (u === 'm/s' || u === 'ms' || u === 'mps' || !u) {
    // SOG from SignalK is usually m/s
    if (num < 40) return num * 1.943844;
  }
  return num;
}

/**
 * @param {string} dataPath - SignalK path (e.g. environment.water.temperature)
 * @param {number|string} rawValue - Raw value from SignalK
 * @param {object} [widgetConfig] - Widget config (unit, decimals, etc.)
 * @param {string} [signalKUnit] - Unit from SignalK payload if present
 * @returns {{ value: number|string, unit: string }}
 */
export function formatSignalKNumeric(dataPath, rawValue, widgetConfig = {}, signalKUnit = '') {
  const scalar = normalizeSignalkScalar(rawValue);
  if (scalar === null) {
    return { value: '--', unit: widgetConfig?.unit || signalKUnit || '' };
  }

  const num = parseNumber(scalar);
  if (num === null) {
    return { value: scalar, unit: widgetConfig?.unit || signalKUnit || '' };
  }

  let unit = widgetConfig?.unit || signalKUnit || '';
  let formattedValue = num;
  const path = dataPath || '';

  if (path.includes('runTime') || path.includes('runtime') || path.includes('Hours')) {
    formattedValue = num / 3600;
    unit = unit || 'h';
  } else if (
    path.includes('revolutions') ||
    path.includes('revolution') ||
    path.includes('rpm') ||
    path.includes('RPM')
  ) {
    formattedValue = num > 200 ? num : num * 60;
    unit = unit || 'RPM';
  } else if (
    path.includes('temperature') ||
    path.includes('Temperature') ||
    path.includes('temp') ||
    path.includes('Temp')
  ) {
    const kelvin = inferKelvin(num, path, signalKUnit);
    const desiredUnit = normalizeUnitToken(widgetConfig?.unit || 'f');

    if (desiredUnit === 'f' || desiredUnit === 'fahrenheit') {
      formattedValue = (kelvin - 273.15) * (9 / 5) + 32;
      unit = 'F';
    } else if (desiredUnit === 'c' || desiredUnit === 'celsius') {
      formattedValue = kelvin - 273.15;
      unit = '°C';
    } else {
      formattedValue = kelvin;
      unit = 'K';
    }
  } else if (path.includes('depth') || path.includes('Depth')) {
    const meters = depthToMeters(num, signalKUnit);
    const desired = normalizeUnitToken(widgetConfig?.unit || unit || 'ft');
    if (desired === 'ft' || desired === 'feet' || desired === 'foot' || desired === "'") {
      formattedValue = meters * METERS_TO_FEET;
      unit = 'ft';
    } else if (desired === 'fathoms' || desired === 'fathom') {
      formattedValue = meters / 1.8288;
      unit = 'fathoms';
    } else {
      formattedValue = meters;
      unit = 'm';
    }
  } else if (path.includes('yield') || path.includes('energy') || path.includes('Energy')) {
    if (num >= 1000) {
      formattedValue = num / 1000;
      unit = unit || 'kWh';
    } else {
      unit = unit || 'Wh';
    }
  } else if (path.includes('power') || path.includes('Power')) {
    if (num >= 1000) {
      formattedValue = num / 1000;
      unit = unit || 'kW';
    } else {
      unit = unit || 'W';
    }
  } else if (path.includes('voltage') || path.includes('Voltage')) {
    unit = unit || 'V';
  } else if (
    (path.includes('speedOverGround') || path.includes('speedThroughWater')) &&
    (unit === 'kts' || unit === 'kn' || widgetConfig?.unit === 'kts')
  ) {
    // SignalK SOG is often m/s
    if (num < 25) {
      formattedValue = num * 1.943844;
      unit = 'kts';
    }
  } else if (
    path.includes('heading') ||
    path.includes('courseOverGround') ||
    path.includes('direction') ||
    path.includes('.yaw')
  ) {
    const deg = headingToDegrees(num, signalKUnit || unit);
    if (deg !== null) {
      formattedValue = deg;
      unit = '°';
    }
  }

  if (typeof formattedValue === 'number' && Number.isFinite(formattedValue)) {
    const decimals = coerceWidgetDecimals(widgetConfig, 1);
    formattedValue = parseFloat(formattedValue.toFixed(decimals));
  }

  return { value: formattedValue, unit };
}

export function walkSignalKPath(sourceData, dataPath) {
  if (!sourceData || !dataPath) return null;
  const pathParts = dataPath.split('.');
  let current = sourceData;
  for (const part of pathParts) {
    if (current && typeof current === 'object' && part in current) {
      current = current[part];
    } else {
      return null;
    }
  }
  if (current && typeof current === 'object' && 'value' in current) {
    return {
      rawValue: current.value,
      signalKUnit: current.unit || '',
      timestamp: current.timestamp,
      resolvedPath: dataPath,
    };
  }
  return null;
}

const TANK_TYPES = ['freshWater', 'wasteWater', 'fuel', 'blackWater', 'grayWater'];

const ELECTRICAL_INSTANCE_GROUPS = [
  'solar',
  'batteries',
  'chargers',
  'alternators',
  'switches',
  'converter',
  'dcdc',
];

/** Flatten hierarchical /signalk/latest tree to dotted paths for browse UI. */
export function flattenSignalkTree(node, prefix = '') {
  const out = {};
  if (!node || typeof node !== 'object' || Array.isArray(node)) return out;

  const isLeaf = (o) =>
    o &&
    typeof o === 'object' &&
    Object.prototype.hasOwnProperty.call(o, 'value') &&
    !Object.prototype.hasOwnProperty.call(o, 'values');

  for (const [key, val] of Object.entries(node)) {
    const path = prefix ? `${prefix}.${key}` : key;
    if (isLeaf(val)) {
      const scalar = normalizeSignalkScalar(val.value);
      out[path] = {
        value: scalar !== null ? scalar : val.value,
        units: val.unit || val.units || '',
        timestamp: val.timestamp,
        source: val.$source || val.source || 'unknown',
      };
    } else if (val && typeof val === 'object') {
      Object.assign(out, flattenSignalkTree(val, path));
    }
  }
  return out;
}

function sortElectricalPaths(paths) {
  return [...paths].sort((a, b) => {
    const idA = parseInt(a.split('.')[2], 10);
    const idB = parseInt(b.split('.')[2], 10);
    if (!Number.isNaN(idA) && !Number.isNaN(idB)) return idA - idB;
    return a.localeCompare(b);
  });
}

function electricalInstancePaths(sourceData, group, suffix) {
  const g = sourceData?.electrical?.[group];
  if (!g || typeof g !== 'object') return [];
  return sortElectricalPaths(
    Object.keys(g).map((id) => `electrical.${group}.${id}.${suffix}`)
  );
}

function listElectricalPaths(sourceData, group, suffix) {
  const hierarchical = electricalInstancePaths(sourceData, group, suffix);
  if (hierarchical.length > 0) return hierarchical;
  const flat = flattenSignalkTree(sourceData);
  const prefix = `electrical.${group}.`;
  const suffixNeedle = `.${suffix}`;
  return sortElectricalPaths(
    Object.keys(flat).filter((k) => k.startsWith(prefix) && k.endsWith(suffixNeedle))
  );
}

function nodeFromFlatEntry(flatEntry, resolvedPath) {
  if (!flatEntry) return null;
  return {
    rawValue: flatEntry.value,
    signalKUnit: flatEntry.units || flatEntry.unit || '',
    timestamp: flatEntry.timestamp,
    resolvedPath,
  };
}

/** Victron/Cerbo often use IDs like 279-second (starter) and 279-first (house). */
function batteryRoleMatchers(role) {
  const roleLower = String(role || '').toLowerCase();
  const matchers = {
    starter: (id) => /second|starter|start|engine|crank/i.test(id),
    house: (id) => /first|house|service|domestic|housebank/i.test(id),
    main: (id) => /first|house|main|service|domestic/i.test(id),
    primary: (id) => /first|primary|house|main/i.test(id),
    secondary: (id) => /second|secondary|starter/i.test(id),
  };
  return matchers[roleLower] || ((id) => id.toLowerCase().includes(roleLower));
}

function batteryRolePaths(dataPath, availablePaths) {
  const m = dataPath.match(
    /^electrical\.batteries\.(starter|house|main|primary|secondary)\.(.+)$/i
  );
  if (!m) return [];
  const [, role, suffix] = m;
  const matchFn = batteryRoleMatchers(role);
  const prefix = 'electrical.batteries.';
  return availablePaths.filter((p) => {
    if (!p.startsWith(prefix) || !p.endsWith(`.${suffix}`)) return false;
    const instanceId = p.slice(prefix.length, -(suffix.length + 1));
    return matchFn(instanceId);
  });
}

function matchElectricalInstancePath(dataPath) {
  return dataPath.match(
    /^electrical\.(solar|batteries|chargers|alternators|switches|converter|dcdc)\.([^.]+)\.(.+)$/
  );
}

function tankInstancePaths(sourceData, tankType, suffix) {
  const group = sourceData?.tanks?.[tankType];
  if (!group || typeof group !== 'object') return [];
  return Object.keys(group)
    .filter((id) => id !== 'main')
    .map((id) => `tanks.${tankType}.${id}.${suffix}`)
    .sort((a, b) => {
      const idA = parseInt(a.split('.')[2], 10);
      const idB = parseInt(b.split('.')[2], 10);
      if (!Number.isNaN(idA) && !Number.isNaN(idB)) return idA - idB;
      return a.localeCompare(b);
    });
}

/**
 * Resolve widget paths like tanks.freshWater.main.currentLevel to tanks.freshWater.0.currentLevel
 */
export function resolveSignalKNode(sourceData, dataPath) {
  const exact = walkSignalKPath(sourceData, dataPath);
  if (exact) return exact;

  const flat = flattenSignalkTree(sourceData);
  const flatExact = nodeFromFlatEntry(flat[dataPath], dataPath);
  if (flatExact) return flatExact;

  if (isConcreteElectricalInstancePath(dataPath)) {
    return null;
  }

  const mainMatch = dataPath.match(
    /^tanks\.(freshWater|wasteWater|fuel|blackWater|grayWater)\.main\.(.+)$/
  );
  if (mainMatch) {
    const [, tankType, suffix] = mainMatch;
    for (const candidate of tankInstancePaths(sourceData, tankType, suffix)) {
      const node = walkSignalKPath(sourceData, candidate);
      if (node) return node;
    }
  }

  // Legacy templates used wasteWater.0; many boats use .1, .21, etc.
  if (dataPath === 'tanks.wasteWater.0.currentLevel') {
    for (const candidate of tankInstancePaths(sourceData, 'wasteWater', 'currentLevel')) {
      const node = walkSignalKPath(sourceData, candidate);
      if (node) return node;
    }
  }

  const elecMatch = matchElectricalInstancePath(dataPath);
  if (elecMatch) {
    const [, group, , suffix] = elecMatch;
    if (ELECTRICAL_INSTANCE_GROUPS.includes(group)) {
      const candidates = listElectricalPaths(sourceData, group, suffix);
      const roleMatches =
        group === 'batteries' ? batteryRolePaths(dataPath, candidates) : [];
      const ordered = [...roleMatches, ...candidates.filter((p) => !roleMatches.includes(p))];
      for (const candidate of ordered) {
        const node = walkSignalKPath(sourceData, candidate);
        if (node) return { ...node, resolvedPath: candidate };
        const flat = flattenSignalkTree(sourceData);
        const flatNode = nodeFromFlatEntry(flat[candidate], candidate);
        if (flatNode) return flatNode;
      }
    }
  }

  for (const candidate of depthFallbackCandidates(dataPath)) {
    const node = walkSignalKPath(sourceData, candidate);
    if (node) return node;
  }

  if (
    dataPath.startsWith('navigation.heading') ||
    dataPath.startsWith('navigation.courseOverGround')
  ) {
    for (const candidate of [
      'navigation.headingTrue',
      'navigation.headingMagnetic',
      'navigation.attitude.yaw',
      'navigation.courseOverGroundTrue',
      'navigation.courseOverGroundMagnetic',
    ]) {
      if (candidate === dataPath) continue;
      const node = walkSignalKPath(sourceData, candidate);
      if (node) return { ...node, resolvedPath: candidate };
      const flatNode = nodeFromFlatEntry(flat[candidate], candidate);
      if (flatNode) return flatNode;
    }
  }

  const batterySuffixMatch = dataPath.match(/^electrical\.batteries\.[^.]+\.(.+)$/);
  if (batterySuffixMatch) {
    const suffix = batterySuffixMatch[1];
    const allBatteryPaths = Object.keys(flat).filter(
      (k) => k.startsWith('electrical.batteries.') && k.endsWith(`.${suffix}`)
    );
    for (const candidate of batteryRolePaths(dataPath, allBatteryPaths)) {
      const flatNode = nodeFromFlatEntry(flat[candidate], candidate);
      if (flatNode) return flatNode;
    }
  }

  return null;
}

/** Match live WebSocket flat paths (e.g. tanks.freshWater.20.currentLevel) */
export function resolveLiveDataPath(liveData, dataPath) {
  if (!liveData || !dataPath) return null;
  if (liveData[dataPath]) {
    return { entry: liveData[dataPath], resolvedPath: dataPath };
  }

  if (isConcreteElectricalInstancePath(dataPath)) {
    const m = matchElectricalInstancePath(dataPath);
    if (m) {
      const [, group, instanceId, suffix] = m;
      const keys = Object.keys(liveData).filter(
        (k) =>
          k.startsWith(`electrical.${group}.${instanceId}.`) &&
          (k.endsWith(`.${suffix}`) || k === `electrical.${group}.${instanceId}.${suffix}`)
      );
      if (keys.length > 0) {
        const resolvedPath = sortElectricalPaths(keys)[0];
        return { entry: liveData[resolvedPath], resolvedPath };
      }
    }
    return null;
  }

  const mainMatch = dataPath.match(
    /^tanks\.(freshWater|wasteWater|fuel|blackWater|grayWater)\.main\.(.+)$/
  );
  if (mainMatch) {
    const [, tankType, suffix] = mainMatch;
    const suffixNeedle = `.${suffix}`;
    const prefix = `tanks.${tankType}.`;
    const keys = Object.keys(liveData)
      .filter((k) => k.startsWith(prefix) && k.endsWith(suffixNeedle))
      .sort();
    if (keys.length > 0) {
      return { entry: liveData[keys[0]], resolvedPath: keys[0] };
    }
  }

  if (dataPath === 'tanks.wasteWater.0.currentLevel') {
    const keys = Object.keys(liveData)
      .filter((k) => k.startsWith('tanks.wasteWater.') && k.endsWith('.currentLevel'))
      .sort();
    if (keys.length > 0) {
      return { entry: liveData[keys[0]], resolvedPath: keys[0] };
    }
  }

  const elecMatch = matchElectricalInstancePath(dataPath);
  if (elecMatch) {
    const [, group, , suffix] = elecMatch;
    const prefix = `electrical.${group}.`;
    const suffixNeedle = `.${suffix}`;
    const keys = sortElectricalPaths(
      Object.keys(liveData).filter((k) => k.startsWith(prefix) && k.endsWith(suffixNeedle))
    );
    const roleMatches = group === 'batteries' ? batteryRolePaths(dataPath, keys) : [];
    const ordered = [...roleMatches, ...keys.filter((k) => !roleMatches.includes(k))];
    if (ordered.length > 0) {
      return { entry: liveData[ordered[0]], resolvedPath: ordered[0] };
    }
  }

  for (const candidate of depthFallbackCandidates(dataPath)) {
    if (liveData[candidate]) {
      return { entry: liveData[candidate], resolvedPath: candidate };
    }
  }

  return null;
}

function toCoordinateNumber(value) {
  if (value === null || value === undefined) return null;
  const raw = typeof value === 'object' && value !== null && 'value' in value ? value.value : value;
  const num = typeof raw === 'number' ? raw : parseFloat(raw);
  return Number.isFinite(num) ? num : null;
}

function normalizeLatLngPair(lat, lng) {
  if (lat === null || lng === null) return null;
  let la = lat;
  let ln = lng;

  if (Math.abs(la) <= 1.6 && Math.abs(ln) <= 3.3) {
    const laDeg = la * (180 / Math.PI);
    const lnDeg = ln * (180 / Math.PI);
    if (Math.abs(laDeg) <= 90 && Math.abs(lnDeg) <= 180 && (Math.abs(la) < 1.2 || Math.abs(ln) < 2.5)) {
      la = laDeg;
      ln = lnDeg;
    }
  }

  if (Math.abs(la) > 90 && Math.abs(ln) <= 90) {
    const tmp = la;
    la = ln;
    ln = tmp;
  }

  if (Math.abs(la) > 90 || Math.abs(ln) > 180) return null;
  return { latitude: la, longitude: ln };
}

/** Extract { latitude, longitude } from signalk_latest tree or flat path map. */
export function parseSignalkPosition(source) {
  if (!source || typeof source !== 'object') return null;

  const flatPos = source['navigation.position'];
  if (flatPos) {
    const raw = flatPos.value !== undefined ? flatPos.value : flatPos;
    if (raw && typeof raw === 'object') {
      const fixed = normalizeLatLngPair(
        toCoordinateNumber(raw.latitude ?? raw.lat),
        toCoordinateNumber(raw.longitude ?? raw.lng ?? raw.lon)
      );
      if (fixed) return fixed;
    }
  }

  const flatLat = toCoordinateNumber(source['navigation.position.latitude']);
  const flatLon = toCoordinateNumber(source['navigation.position.longitude']);
  const fromFlat = normalizeLatLngPair(flatLat, flatLon);
  if (fromFlat) return fromFlat;

  const nav = source.navigation;
  if (!nav) return null;

  const pos = nav.position;
  if (pos) {
    if (pos.value && typeof pos.value === 'object') {
      const fixed = normalizeLatLngPair(
        toCoordinateNumber(pos.value.latitude ?? pos.value.lat),
        toCoordinateNumber(pos.value.longitude ?? pos.value.lng ?? pos.value.lon)
      );
      if (fixed) return fixed;
    }

    const fixed = normalizeLatLngPair(
      toCoordinateNumber(pos.latitude),
      toCoordinateNumber(pos.longitude)
    );
    if (fixed) return fixed;
  }

  return null;
}

const HEADING_PATHS = [
  'navigation.headingTrue',
  'navigation.headingMagnetic',
  'navigation.attitude.yaw',
  'navigation.courseOverGroundTrue',
  'navigation.courseOverGroundMagnetic',
];

const SPEED_PATHS = ['navigation.speedOverGround', 'navigation.speedThroughWater'];

const DEPTH_PATHS = [
  'environment.depth.belowTransducer',
  'environment.depth.belowKeel',
  'environment.depth.belowSurface',
  'navigation.depthBelowTransducer',
];

/** Best available depth in meters. */
export function parseSignalkDepth(source) {
  for (const path of DEPTH_PATHS) {
    const reading = readPathValue(source, path);
    if (reading?.value !== null) {
      const meters = depthToMeters(reading.value, reading.unit);
      if (meters !== null) return { depth: meters, path };
    }
  }
  return null;
}

function readAttitudeYaw(source) {
  if (!source || typeof source !== 'object') return null;

  const yawFlat = source['navigation.attitude.yaw'];
  if (yawFlat !== undefined) {
    const raw = yawFlat?.value !== undefined ? yawFlat.value : yawFlat;
    const num = toCoordinateNumber(raw);
    if (num !== null) {
      return { value: num, unit: yawFlat?.unit || yawFlat?.units || 'rad' };
    }
  }

  const attFlat = source['navigation.attitude'];
  const attNav = source.navigation?.attitude;
  for (const att of [attFlat, attNav]) {
    if (!att || typeof att !== 'object') continue;

    const container = att.value !== undefined && typeof att.value === 'object' ? att.value : att;
    const yawRaw =
      container?.yaw?.value !== undefined
        ? container.yaw.value
        : container?.yaw;
    const num = toCoordinateNumber(yawRaw);
    if (num !== null) {
      const unit =
        container?.yaw?.unit ||
        container?.yaw?.units ||
        att?.unit ||
        att?.units ||
        'rad';
      return { value: num, unit };
    }
  }

  return null;
}

function readPathValue(source, path) {
  if (!source || typeof source !== 'object') return null;

  const flat = source[path];
  if (flat !== undefined) {
    const raw = flat?.value !== undefined ? flat.value : flat;
    return {
      value: toCoordinateNumber(raw),
      unit: flat?.unit || flat?.units || '',
    };
  }

  const parts = path.split('.');
  let cur = source;
  for (const part of parts) {
    if (!cur || typeof cur !== 'object' || !(part in cur)) return null;
    cur = cur[part];
  }
  if (cur && typeof cur === 'object' && 'value' in cur) {
    return { value: toCoordinateNumber(cur.value), unit: cur.unit || cur.units || '' };
  }
  const num = toCoordinateNumber(cur);
  return num === null ? null : { value: num, unit: '' };
}

/** Best available heading in degrees (true → magnetic → attitude yaw → COG). */
export function parseSignalkHeading(source) {
  for (const path of HEADING_PATHS) {
    const reading =
      path === 'navigation.attitude.yaw'
        ? readAttitudeYaw(source)
        : readPathValue(source, path);
    if (reading?.value !== null) {
      const deg = headingToDegrees(reading.value, reading.unit);
      if (deg !== null) return { heading: deg, path };
    }
  }

  // Flat tree fallback (Cerbo sometimes stores paths only in flattened latest)
  const flat = flattenSignalkTree(source);
  for (const path of HEADING_PATHS) {
    if (path === 'navigation.attitude.yaw') {
      const attEntry = flat['navigation.attitude'];
      if (attEntry) {
        const fromAtt = readAttitudeYaw({ 'navigation.attitude': attEntry });
        if (fromAtt?.value !== null) {
          const deg = headingToDegrees(fromAtt.value, fromAtt.unit);
          if (deg !== null) return { heading: deg, path: 'navigation.attitude.yaw' };
        }
      }
      const yawEntry = flat['navigation.attitude.yaw'];
      if (yawEntry) {
        const flatNode = nodeFromFlatEntry(yawEntry, 'navigation.attitude.yaw');
        if (flatNode) {
          const deg = headingToDegrees(flatNode.rawValue, flatNode.signalKUnit);
          if (deg !== null) return { heading: deg, path: 'navigation.attitude.yaw' };
        }
      }
      continue;
    }
    const entry = flat[path];
    if (entry) {
      const flatNode = nodeFromFlatEntry(entry, path);
      if (flatNode) {
        const deg = headingToDegrees(flatNode.rawValue, flatNode.signalKUnit);
        if (deg !== null) return { heading: deg, path };
      }
    }
  }

  return null;
}

/** Best available speed in knots. */
export function parseSignalkSpeed(source) {
  for (const path of SPEED_PATHS) {
    const reading = readPathValue(source, path);
    if (reading?.value !== null) {
      const kts = speedToKnots(reading.value, reading.unit);
      if (kts !== null) return { speed: kts, path };
    }
  }
  return null;
}

/** Combined navigation fix for maps, trips, and auto-tracking. */
export function parseSignalkNavigation(source) {
  const position = parseSignalkPosition(source);
  const headingInfo = parseSignalkHeading(source);
  const speedInfo = parseSignalkSpeed(source);
  const depthInfo = parseSignalkDepth(source);
  if (!position && !headingInfo && !speedInfo && !depthInfo) return null;
  return {
    ...position,
    heading: headingInfo?.heading ?? null,
    headingPath: headingInfo?.path ?? null,
    speed: speedInfo?.speed ?? null,
    speedPath: speedInfo?.path ?? null,
    depth: depthInfo?.depth ?? null,
    depthPath: depthInfo?.path ?? null,
  };
}
